import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './Navigation.css';

interface NavigationProps {
  onSectionChange: (section: string) => void;
}

export const Navigation: React.FC<NavigationProps> = ({ onSectionChange }) => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const baseNavItems = [
    { section: 'overview', label: 'Overview →', color: 'blue' },
    { section: 'transportation', label: 'Transportation →', color: 'light-blue' },
    { section: 'buildings', label: 'Buildings →', color: 'gold' },
    { section: 'other', label: 'Waste +', color: 'orange' },
    { section: 'people', label: 'People →', color: 'pink' },
    { section: 'ghg', label: 'GHG inventory →', color: 'teal' }
  ];

  // Policy Tool is now available to all users
  const navItems = [
    ...baseNavItems,
    { section: 'policy-tool', label: 'Policy Tool →', color: 'purple' }
  ];

  const handleNavClick = (section: string) => {
    const knownSections = new Set([
      'overview',
      'transportation',
      'buildings',
      'other',
      'people',
      'ghg',
      'policy-tool'
    ]);

    const parts = location.pathname.split('/').filter(Boolean);
    const isOverviewPath =
      parts.length === 0 ||
      (parts.length === 1 && !knownSections.has(parts[0])) ||
      (parts.length === 2 && parts[1] === 'overview');

    // Check if we're on Overview page and if it's fully loaded
    const isOnOverviewPage = isOverviewPath;
    const overviewFullyLoaded = typeof window !== 'undefined' && (window as any).overviewFullyLoaded === true;
    
    // If on Overview page and not fully loaded, block navigation to pages other than Transportation
    if (isOnOverviewPage && !overviewFullyLoaded && section !== 'transportation' && section !== 'overview') {
      alert('Please wait until the Overview page fully loads before navigating to other pages. The Transportation page is available.');
      return;
    }
    
    onSectionChange(section);
    // Close mobile menu when navigating
    setIsMobileMenuOpen(false);
    // Scroll to top when navigating to a new section
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getCurrentSection = () => {
    const knownSections = new Set([
      'overview',
      'transportation',
      'buildings',
      'other',
      'people',
      'ghg',
      'policy-tool'
    ]);

    const parts = location.pathname.split('/').filter(Boolean);
    if (parts.length === 0) return 'overview';
    if (parts.length === 1) {
      return knownSections.has(parts[0]) ? parts[0] : 'overview';
    }
    return parts[1] || 'overview';
  };

  const currentSection = getCurrentSection();

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobileMenuOpen) {
        const target = event.target as Element;
        if (!target.closest('.nav-bar')) {
          setIsMobileMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  return (
    <div className="nav-bar">
      {/* Mobile Title */}
      <div className="mobile-nav-title">
        ClimatePlans
      </div>

      {/* Mobile Hamburger Button */}
      <button 
        className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle navigation menu"
      >
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
      </button>

      {/* Navigation Items */}
      <div className={`nav-items ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        {navItems.map((item) => (
          <div
            key={item.section}
            className={`nav-item ${item.color} ${currentSection === item.section ? 'active' : ''}`}
            onClick={() => handleNavClick(item.section)}
          >
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
};
